/**
 * Style plumbing for shadow-DOM-based widgets.
 *
 * Design tokens and component CSS are deliberately handled in opposite ways:
 *
 * - **Design tokens** (`--config-*`) are never declared inside a shadow root. Custom
 *   properties inherit through the shadow boundary, so a widget picks up whatever the
 *   document declares and tracks later edits — theme settings, live preview — for free.
 *   Declaring them on `:host` instead *pins* them at construction time, because a
 *   `:host` match outranks an inherited value; that is what used to freeze widget
 *   colours until a reload.
 * - **Component CSS** carries global element selectors, so it stays shadow-scoped.
 *
 * Two contexts:
 * - **Community pages** — tokens are already on `html` via the server-rendered
 *   `#css-variables` style. Nothing to inject; inheritance does the work.
 * - **Embed on an external host page** — the host document has no tokens of its own,
 *   so they are promoted *once* into `document.head` by `promoteDesignTokens`, scoped
 *   by selector to our own custom elements so the host page's styles are untouched.
 */

/** Whitelisted font CDN hostnames whose stylesheets need document-level @font-face registration. */
const FONT_HOSTS = ['fonts.googleapis.com', 'fonts.gstatic.com']

/** `id` of the server-rendered style holding the raw `html { --config-*: … }` token block. */
const TOKEN_SOURCE_STYLE_ID = 'css-variables'

/** `id` of the single document-level style this module maintains on embed host pages. */
const TOKEN_STYLE_ID = 'gs-cc-design-tokens'

/**
 * Tokens are scoped to our custom elements rather than `:root`/`html` so that embedding
 * on a customer's site cannot introduce `--config-*` into their own cascade. Custom
 * properties inherit from any ancestor, and every widget lives inside one of these, so
 * scoping costs nothing.
 */
const TOKEN_SCOPE_SELECTOR = ['gs-cc-embed-widget', 'gs-cc-widget', 'gs-cc-registry-widget', 'gs-cc-ai-widget'].join(
  ', '
)

function isFontStylesheet(href: string): boolean {
  try {
    return FONT_HOSTS.includes(new URL(href).hostname)
  } catch {
    return false
  }
}

interface HeadStylesheet {
  href: string
  attributes?: Record<string, string>
}
interface HeadStyle {
  id?: string
  content: string
}
interface HeadResources {
  stylesheets?: HeadStylesheet[]
  styles?: HeadStyle[]
}

/**
 * Publishes the community's design tokens into the *host document* as a single style,
 * re-scoped from `html` to our custom elements.
 *
 * Only used on external host pages (embed). Idempotent and safe to call repeatedly —
 * later calls update the existing style in place, so one write re-themes every widget
 * on the page at once.
 *
 * Any CSS preceding the token block is preserved verbatim: `getStyleConfigurationAsCss`
 * prepends the configured font declarations there, and `@font-face` has to stay at
 * document level to register reliably.
 */
export function promoteDesignTokens(cssText: string): void {
  // `html` is what the server emits; `:host` covers a host page that pre-populated
  // `headResources` with content already scoped for shadow injection — at document
  // level that selector would match nothing.
  const scoped = cssText.replace(/(?:html|:host)\s*\{/, `${TOKEN_SCOPE_SELECTOR} {`)

  const existing = document.getElementById(TOKEN_STYLE_ID)
  if (existing instanceof HTMLStyleElement) {
    if (existing.textContent !== scoped) existing.textContent = scoped
    return
  }

  const style = document.createElement('style')
  style.id = TOKEN_STYLE_ID
  style.textContent = scoped
  document.head.appendChild(style)
}

/**
 * Injects the component CSS a widget needs into its shadow root.
 *
 * Design tokens are explicitly *not* injected — see the module comment. On embed host
 * pages the stylesheets come from the resource-loader's parsed head resources; on
 * community pages they are cloned from `document.head`.
 */
export function injectWidgetStyles(shadowRoot: ShadowRoot): void {
  const embedResources = (window as any).__insidedEmbed?.headResources as HeadResources | undefined

  if (embedResources) {
    injectHeadResources(shadowRoot, embedResources)
    return
  }

  injectDocumentStylesheets(shadowRoot)
}

function createStylesheetLink(href: string, attributes?: Record<string, string>): HTMLLinkElement {
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  if (attributes) {
    for (const [key, value] of Object.entries(attributes)) {
      link.setAttribute(key, value)
    }
  }
  return link
}

function injectStylesheetIntoShadow(shadowRoot: ShadowRoot, { href, attributes }: HeadStylesheet): void {
  if (shadowRoot.querySelector(`link[href="${href}"]`)) return
  shadowRoot.prepend(createStylesheetLink(href, attributes))
}

/**
 * External font stylesheets must also be registered on document.head because
 * @font-face rules loaded exclusively inside shadow DOM are not reliably
 * applied globally across all browsers.
 */
function registerFontStylesheetGlobally({ href, attributes }: HeadStylesheet): void {
  if (!isFontStylesheet(href)) return
  if (document.head.querySelector(`link[href="${href}"]`)) return
  document.head.appendChild(createStylesheetLink(href, attributes))
}

function injectInlineStyle(shadowRoot: ShadowRoot, { id, content }: HeadStyle): void {
  if (id && shadowRoot.getElementById(id)) return
  const style = document.createElement('style')
  if (id) style.id = id
  style.textContent = content
  shadowRoot.prepend(style)
}

/**
 * Embed path: the resource-loader stores parsed head resources (stylesheets, styles)
 * in window.__insidedEmbed.headResources.
 *
 * A token block reaching us here — a host that pre-populated `headResources` itself,
 * rather than letting the resource-loader promote it — is redirected to document level
 * instead of being scoped into this one shadow root.
 */
function injectHeadResources(shadowRoot: ShadowRoot, res: HeadResources): void {
  for (const stylesheet of res.stylesheets ?? []) {
    injectStylesheetIntoShadow(shadowRoot, stylesheet)
    registerFontStylesheetGlobally(stylesheet)
  }

  for (const style of res.styles ?? []) {
    if (style.id === TOKEN_SOURCE_STYLE_ID) {
      promoteDesignTokens(style.content)
      continue
    }
    injectInlineStyle(shadowRoot, style)
  }
}

/**
 * Community page path: clone the document's stylesheets into the shadow root so widget
 * markup resolves against the same component CSS it would in the light DOM.
 *
 * The token style is skipped on purpose — it stays inherited rather than copied.
 */
function injectDocumentStylesheets(shadowRoot: ShadowRoot): void {
  const links = document.head.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')
  for (const link of links) {
    const href = link.getAttribute('href')
    if (!href) continue
    if (shadowRoot.querySelector(`link[href="${href}"]`)) continue
    shadowRoot.prepend(link.cloneNode(true))
  }
}
